'use client';

import { HTMLAttributes, ComponentProps, forwardRef, ElementRef } from 'react';
import { Root, Trigger, Portal, Overlay, Content, Title } from '@radix-ui/react-dialog';
import clsx from 'clsx';

export const Dialog = Root;

export const DialogTrigger = Trigger;

export const DialogPortal = Portal;

// DialogOverlay is a component that renders the overlay for the dialog.
export function DialogOverlay({ className, ...props }: ComponentProps<typeof Overlay>) {
  return (
    <Overlay
      className={clsx(
        'fixed inset-[0px] z-dialog bg-transparent-floating-base data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}

// DialogContent is a component that renders the content for the dialog.
export const DialogContent = forwardRef<ElementRef<typeof Content>, ComponentProps<typeof Content>>(
  function DialogContent({ className, children, ...props }, ref) {
    return (
      <DialogPortal>
        <DialogOverlay />
        <Content
          ref={ref}
          className={clsx(
            'fixed left-[50%] top-[50%] z-dialog w-full max-w-[calc(100vw-32px)] max-h-[calc(var(--screen-height)-32px)] pc:max-w-[640px] translate-x-[-50%] translate-y-[-50%] border border-neutral-auxiliary bg-base-ground shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] flex flex-col rounded-medium',
            className
          )}
          {...props}
        >
          {children}
        </Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = Content.displayName;

// DialogHeader is a component that renders the header for the dialog.
export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'flex flex-row justify-between text-left border-b-thin border-neutral-auxiliary p-4 gap-3',
        className
      )}
      {...props}
    />
  );
}

// DialogFooter is a component that renders the footer for the dialog.
export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'flex flex-col justify-end p-4 border-t-thin border-neutral-auxiliary',
        className
      )}
      {...props}
    />
  );
}

// DialogTitle is a component that renders the title for the dialog.
export function DialogTitle({ className, ...props }: ComponentProps<typeof Title>) {
  return (
    <Title className={clsx('text-medium font-bold text-neutral-primary ', className)} {...props} />
  );
}

// DialogBody is a component that renders the body of the dialog content
export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'text-medium text-neutral-primary overflow-y-auto flex flex-col p-6 gap-8',
        className
      )}
      {...props}
    />
  );
}
